// The per-type buckets `store.payment` carries. `checkout-store.ts#create` builds its buckets
// from this list, so it is the single runtime source for "which types have a bucket": submit
// reads `store.payment[type].token`, and this is the set of types that read is defined for.
export const STORE_PAYMENT_TYPES = ['payment-card', 'paypal', 'apple-pay', 'Klarna'] as const
export type StorePaymentType = (typeof STORE_PAYMENT_TYPES)[number]

// `splitit` is a presentation-only tab: it collects a card and attaches an installment
// plan, so the store never holds it — it holds `payment-card` plus a virtualInstallmentId.
const SPLITIT = 'splitit'

const toStoreType = (type: string | undefined): string | undefined => (type === SPLITIT ? 'payment-card' : type)
const hasStoreBucket = (type: string | undefined): type is StorePaymentType =>
  (STORE_PAYMENT_TYPES as readonly string[]).includes(type)

// The payment type the checkout should start on.
//
// A contact's saved payment method is a good default, but only when this checkout can
// actually collect it: the type has to be one this checkout offers (`enabledPayments`
// drives the rendered tabs) *and* one the store has a bucket for (`store.payment[type]` is
// what mounting, validation and submit read). `enabledPayments` alone is not enough — a
// merchant can enable `ach`, which this element renders no tab for and the store has no
// bucket for. A saved type that fails either test — PayPal vaulted on an earlier order when
// this checkout is card-only, ACH, a method the workspace has since turned off — would
// otherwise leave `payment.type` pointing at something with no tab behind it while the
// markup highlights `enabledPayments[0]`; nothing reconciles the two, so clicking the
// highlighted tab is a no-op (the click handler returns early on the already-selected tab).
//
// `savedPaymentType` is whatever `Billing::PaymentMethod#payment_method_type` returned, so
// it is not narrowed to the types this element renders.
//
// Always returns a key of `store.payment`. When nothing this element can collect is enabled
// (an ACH-only funnel, an Apple-Pay-only checkout page whose card list is empty), a saved
// method still submits by id — and submit reads `store.payment[type]` before it looks at the
// id — so the type has to stay a real bucket: the saved one when it has a bucket, which is
// what the checkout did before this resolver existed, otherwise card, whose bucket always
// exists. `getInitialCriticalErrors` is what reports a checkout with no payment methods.
export const resolveInitialPaymentType = (
  savedPaymentType: string | undefined,
  enabledPayments: string[]
): StorePaymentType => {
  const collectable = (enabledPayments ?? []).map(toStoreType).filter(hasStoreBucket)
  const saved = toStoreType(savedPaymentType)

  if (collectable.length) return collectable.find((type) => type === saved) ?? collectable[0]

  return hasStoreBucket(saved) ? saved : 'payment-card'
}
